/**
 * QuotaWarningBanner Component
 *
 * Part of Story 3.2: Quota Warning Banner (80%)
 * Epic 3: Frontend UI Components
 *
 * Displays a non-intrusive banner at the top of the dashboard when
 * user reaches 80% of their monthly execution quota.
 *
 * Acceptance Criteria:
 * - AC#1: Banner appears at top of dashboard when usage >= 80%
 * - AC#2: Message: "ℹ️ You've used [X] of [Y] executions (80%)"
 * - AC#3: Progress bar shows visual quota usage (blue background)
 * - AC#4: CTA: "Upgrade for 500 executions/month" (secondary button)
 * - AC#5: Dismissible with X icon (stores dismissal in localStorage)
 * - AC#6: Show once per day if dismissed
 * - AC#7: Mobile responsive - stacks vertically on small screens
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTierData } from '@/hooks/useTierData';

const DISMISSAL_KEY_PREFIX = 'quota_warning_dismissed_';

/**
 * Get today's date as a string key (YYYY-MM-DD)
 */
function getTodayKey(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Check if banner was dismissed today
 */
function wasDismissedToday(): boolean {
  const todayKey = getTodayKey();
  const dismissalKey = `${DISMISSAL_KEY_PREFIX}${todayKey}`;
  return localStorage.getItem(dismissalKey) === 'true';
}

/**
 * Mark banner as dismissed for today
 */
function markDismissed(): void {
  const todayKey = getTodayKey();
  const dismissalKey = `${DISMISSAL_KEY_PREFIX}${todayKey}`;
  localStorage.setItem(dismissalKey, 'true');

  // Clean up old dismissal keys (keep only last 7 days)
  cleanupOldDismissals();
}

/**
 * Remove old dismissal entries from localStorage
 */
function cleanupOldDismissals(): void {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(DISMISSAL_KEY_PREFIX)) {
      const dateStr = key.replace(DISMISSAL_KEY_PREFIX, '');
      const date = new Date(dateStr);

      if (date < sevenDaysAgo) {
        localStorage.removeItem(key);
      }
    }
  });
}

/**
 * QuotaWarningBanner Component
 *
 * Shows a dismissible banner when user reaches 80% quota usage.
 * Banner is hidden if dismissed within the same day.
 *
 * @example
 * ```tsx
 * // In AppLayout or Dashboard
 * <QuotaWarningBanner />
 * ```
 */
export const QuotaWarningBanner: React.FC = () => {
  const navigate = useNavigate();
  const { data: tierData, isLoading } = useTierData();
  const [isDismissed, setIsDismissed] = useState(false);

  // Check dismissal status on mount
  useEffect(() => {
    setIsDismissed(wasDismissedToday());
  }, []);

  /**
   * Handle banner dismissal
   */
  const handleDismiss = useCallback(() => {
    markDismissed();
    setIsDismissed(true);
  }, []);

  /**
   * Navigate to pricing page
   */
  const handleUpgrade = useCallback(() => {
    navigate('/pricing');
  }, [navigate]);

  // Don't show if loading
  if (isLoading || !tierData) {
    return null;
  }

  const { current_usage = 0, quota_limit = 0, usage_percentage = 0 } = tierData;

  // Only show if usage >= 80%, data is valid, and not dismissed today
  if (!quota_limit || isNaN(usage_percentage) || usage_percentage < 80 || isDismissed) {
    return null;
  }

  return (
    <div
      className="
        relative w-full bg-blue-50 border-b border-blue-200
        px-4 py-3 sm:px-6
        animate-in slide-in-from-top duration-300
      "
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* Info Icon and Message */}
          <div className="flex-1 flex items-start gap-3">
            <span
              className="flex-shrink-0 text-xl sm:text-2xl"
              aria-hidden="true"
            >
              ℹ️
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm sm:text-base font-medium text-blue-900">
                You've used{' '}
                <span className="font-bold">
                  {current_usage} of {quota_limit}
                </span>{' '}
                executions (
                <span className="font-bold">{Math.round(usage_percentage)}%</span>
                )
              </p>

              {/* Progress Bar */}
              <div className="mt-2 w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-full transition-all duration-500 ease-out"
                  style={{ width: `${Math.min(usage_percentage, 100)}%` }}
                  role="progressbar"
                  aria-valuenow={usage_percentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Quota usage: ${Math.round(usage_percentage)}%`}
                />
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={handleUpgrade}
              className="
                flex-1 sm:flex-none
                px-4 py-2 text-sm font-semibold
                text-blue-400 bg-white/[0.06] border border-blue-300
                rounded-lg shadow-sm
                hover:bg-blue-50 hover:border-blue-400
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                transition-colors duration-200
              "
              type="button"
            >
              Upgrade for 500 executions/month
            </button>

            {/* Dismiss Button */}
            <button
              onClick={handleDismiss}
              className="
                flex-shrink-0
                p-2 text-blue-600
                hover:bg-blue-100 rounded-lg
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                transition-colors duration-200
              "
              type="button"
              aria-label="Dismiss quota warning"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

QuotaWarningBanner.displayName = 'QuotaWarningBanner';
